// User-friendly error messages for authentication
export const getAuthErrorMessage = (error: any): string => {
  // Handle network errors
  if (!error.response && error.message === 'Failed to fetch') {
    return 'Unable to connect to the server. Please check your internet connection and try again.';
  }

  // Handle timeout errors
  if (error.code === 'ECONNABORTED' || error.message?.includes('timeout')) {
    return 'The request timed out. Please try again.';
  }

  // Get the error details
  const status = error.response?.status;
  const errorData = error.response?.data;
  const errorMessage = errorData?.detail || error.message || '';
  
  // Ensure errorMessage is a string
  const messageStr = typeof errorMessage === 'string' ? errorMessage : String(errorMessage || '');

  // Handle specific HTTP status codes
  switch (status) {
    case 400:
      if (messageStr.toLowerCase().includes('invalid credentials')) {
        return 'The email or password you entered is incorrect. Please try again.';
      }
      if (messageStr.toLowerCase().includes('email')) {
        return 'Please enter a valid email address.';
      }
      if (messageStr.toLowerCase().includes('password')) {
        return 'Password must be at least 8 characters long.';
      }
      if (messageStr.toLowerCase().includes('company')) {
        return 'Please check your company information and try again.';
      }
      return 'Please check your information and try again.';

    case 401:
      if (messageStr.toLowerCase().includes('invalid credentials')) {
        return 'The email or password you entered is incorrect. Please try again.';
      }
      if (messageStr.toLowerCase().includes('not verified')) {
        return 'Please verify your email address before signing in. Check your inbox for a verification link.';
      }
      if (messageStr.toLowerCase().includes('disabled') || messageStr.toLowerCase().includes('inactive')) {
        return 'Your account has been disabled. Please contact support for assistance.';
      }
      return 'Authentication failed. Please check your credentials and try again.';

    case 403:
      return 'Access denied. You don\'t have permission to perform this action.';

    case 404:
      return 'Service not found. Please try again later or contact support.';

    case 409:
      if (messageStr.toLowerCase().includes('email')) {
        return 'An account with this email address already exists. Try signing in instead.';
      }
      if (messageStr.toLowerCase().includes('company')) {
        return 'A company with this name already exists. Please choose a different name.';
      }
      return 'This information is already in use. Please try different details.';

    case 422:
      if (messageStr.toLowerCase().includes('email')) {
        return 'Please enter a valid email address.';
      }
      if (messageStr.toLowerCase().includes('password')) {
        return 'Password must be at least 8 characters long and contain letters and numbers.';
      }
      return 'Please check your information and try again.';

    case 429:
      return 'Too many attempts. Please wait a few minutes before trying again.';

    case 500:
      return 'Something went wrong on our end. Please try again in a few moments.';

    case 502:
    case 503:
    case 504:
      return 'The service is temporarily unavailable. Please try again in a few moments.';

    default:
      // Handle specific error messages regardless of status code
      if (messageStr) {
        const lowerMessage = messageStr.toLowerCase();
        
        if (lowerMessage.includes('invalid credentials') || lowerMessage.includes('incorrect password')) {
          return 'The email or password you entered is incorrect. Please try again.';
        }
        
        if (lowerMessage.includes('user not found') || lowerMessage.includes('account not found')) {
          return 'No account found with this email address. Please check your email or create a new account.';
        }
        
        if (lowerMessage.includes('email already exists') || lowerMessage.includes('user already exists')) {
          return 'An account with this email address already exists. Try signing in instead.';
        }
        
        if (lowerMessage.includes('invalid email')) {
          return 'Please enter a valid email address.';
        }
        
        if (lowerMessage.includes('password too short') || lowerMessage.includes('password must be')) {
          return 'Password must be at least 8 characters long.';
        }
        
        if (lowerMessage.includes('company name') && lowerMessage.includes('required')) {
          return 'Company name is required.';
        }
        
        if (lowerMessage.includes('verification') || lowerMessage.includes('not verified')) {
          return 'Please verify your email address before signing in. Check your inbox for a verification link.';
        }
        
        if (lowerMessage.includes('google') && lowerMessage.includes('failed')) {
          return 'Google sign-in failed. Please try again or use email and password.';
        }
        
        if (lowerMessage.includes('session expired') || lowerMessage.includes('token expired')) {
          return 'Your session has expired. Please sign in again.';
        }
        
        // If it's a reasonably user-friendly message, use it
        if (messageStr.length < 100 && !lowerMessage.includes('internal') && !lowerMessage.includes('server error')) {
          return messageStr;
        }
      }
      
      return 'Something went wrong. Please try again or contact support if the problem persists.';
  }
};

// Specific error messages for different auth actions
export const getLoginErrorMessage = (error: any): string => {
  const baseMessage = getAuthErrorMessage(error);
  
  // Add context-specific hints for login
  if (baseMessage.includes('incorrect')) {
    return `${baseMessage} If you forgot your password, you can reset it.`;
  }
  
  if (baseMessage.includes('not found')) {
    return `${baseMessage} You can create a new account if you don't have one yet.`;
  }
  
  return baseMessage;
};

export const getSignupErrorMessage = (error: any): string => {
  const baseMessage = getAuthErrorMessage(error);
  
  // Add context-specific hints for signup
  if (baseMessage.includes('already exists')) {
    return `${baseMessage} You can sign in with your existing account.`;
  }
  
  return baseMessage;
};

export const getGoogleAuthErrorMessage = (error: any): string => {
  const errorMessage = error.message || error.detail || '';
  
  if (errorMessage.includes('popup_closed_by_user')) {
    return 'Google sign-in was cancelled. Please try again.';
  }
  
  if (errorMessage.includes('popup_blocked')) {
    return 'Pop-up was blocked. Please allow pop-ups for this site and try again.';
  }
  
  if (errorMessage.includes('network_error')) {
    return 'Network error during Google sign-in. Please check your connection and try again.';
  }
  
  if (errorMessage.includes('invalid_client')) {
    return 'Google authentication is not properly configured. Please contact support.';
  }
  
  return getAuthErrorMessage(error);
};
